import { setupHeavyAdInterventionObserver } from '@web-rendering/groundcontrol-utils';
import AdUnitComponent from '@web-rendering/groundcontrol-ad-unit/components/ad-unit/ad-unit-new.component';
import type { ButlerResponse } from '@web-rendering/groundcontrol-shared-types';
import type { ProviderWithProps } from '@web-rendering/groundcontrol-core';
import {
  ConfigurationProvider,
  PayloadProvider,
  TrackerServiceProvider,
  type InitOptions,
  type Payload
} from '@web-rendering/groundcontrol-core';
import { resolveEqtDco } from '@web-rendering/groundcontrol-eqt';
import { reportError } from '@web-rendering/groundcontrol-monitoring';
import { setRenderStart } from '@web-rendering/groundcontrol-store';
import {
  getRawPayload,
  isTestEnvironment,
  iterateAndObserveEquativTrackersPerformanceEntries,
  observeFirstPartyEquativTrackersPerformanceEntries
} from '@web-rendering/groundcontrol-utils';
import './ad-unit.scss';
// Native clickouts use the responsive ad-unit layout (responsiveTemplate adds
// `.str-responsive ...` classes). Pull responsive.scss explicitly — previously
// it leaked in via the ad-experience barrel's Banner side-effect.
import '@web-rendering/groundcontrol-ad-unit/tag/models/ad-experiences/responsive.scss';

const renderAdExperience = (
  placeholder: Element,
  response: ButlerResponse | null,
  payload: Payload,
  options: InitOptions
): void => {
  const renderService = options.renderService;

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const providers: ProviderWithProps<any>[] = [
    [ConfigurationProvider, { configuration: options.configurationService }],
    [PayloadProvider, { payload }],
    [TrackerServiceProvider, { id: payload.id, trackerService: options.trackerService }]
  ];
  renderService?.render(response, payload, placeholder, providers, AdUnitComponent);
};

const bootAd = async (placeholder: HTMLElement, options: InitOptions): Promise<void> => {
  const id = placeholder.getAttribute('data-gc-id') ?? placeholder.getAttribute('data-stx-response-name');
  if (!id) {
    reportError(new Error('Missing data-gc-id or data-stx-response-name attribute'), {
      context: 'tag/ad:bootAd/missing response name',
      placeholderAttributes: placeholder.dataset || 'missing'
    });

    return;
  }

  let payload: Payload | null = null;
  try {
    setRenderStart();

    const rawPayload = getRawPayload(id);

    payload = options.payloadService.initialize(rawPayload);

    setupHeavyAdInterventionObserver(id, placeholder, options.trackerService, {
      callerId: payload.network.callerId,
      partnerId: payload.creative.partnerId,
      networkId: payload.network.networkId
    });
    options.trackerService.mergeMetadata({
      payload
    });

    if (isTestEnvironment()) {
      iterateAndObserveEquativTrackersPerformanceEntries(payload);
      observeFirstPartyEquativTrackersPerformanceEntries();
    }
  } catch (e) {
    if (e instanceof Error) {
      reportError(e, {
        context: 'tag/ad:bootAd/fetch ad',
        id: id,
        payload,
        placeholderAttributes: placeholder.dataset || 'missing',
        partnerId: payload?.creative.partnerId,
        networkId: payload?.network.networkId,
        callerId: payload?.network.callerId
      });
    }
    return;
  }

  if (payload.creative.assetsurl) {
    await resolveEqtDco(payload);
  }

  options.trackerService.trackImpressionReceived();

  try {
    renderAdExperience(placeholder, null, payload, options);
  } catch (e) {
    if (e instanceof Error) {
      reportError(e, {
        context: 'tag/ad:bootAd/ad rendering',
        payload,
        partnerId: payload?.creative.partnerId,
        networkId: payload?.network.networkId,
        callerId: payload?.network.callerId
      });
    }
  }
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
(window as any).bootAd = bootAd;

export default bootAd;
